import React, { useState, useEffect } from 'react';
import { auth, db } from '../utils/firebase';
import { updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import { DollarSign } from 'lucide-react';

const UserProfile = () => {
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [autosaveEnabled, setAutosaveEnabled] = useState(true);
  const [autoPdfEnabled, setAutoPdfEnabled] = useState(true);
  const [defaultPaymentStatus, setDefaultPaymentStatus] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Load current display name and preferences if they exist
    const loadUserData = async () => {
      if (auth.currentUser) {
        if (auth.currentUser.displayName) {
          setDisplayName(auth.currentUser.displayName);
        }
        
        // Load user preferences
        try {
          const userPrefsDoc = await getDoc(doc(db, 'userPreferences', auth.currentUser.uid));
          if (userPrefsDoc.exists()) {
            const prefs = userPrefsDoc.data();
            setAutosaveEnabled(prefs.autosaveEnabled ?? true);
            setAutoPdfEnabled(prefs.autoPdfEnabled ?? true);
            setDefaultPaymentStatus(prefs.defaultPaymentStatus ?? true);
          }
        } catch (error) {
          console.error('Error loading user preferences:', error);
        }
      }
    };
    
    loadUserData();
  }, []);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update display name
      await updateProfile(auth.currentUser, {
        displayName: displayName.trim()
      });
      
      // Update user preferences
      await setDoc(doc(db, 'userPreferences', auth.currentUser.uid), {
        autosaveEnabled,
        autoPdfEnabled,
        defaultPaymentStatus
      }, { merge: true });
      
      toast.success('Profile updated successfully');
      // Wait a brief moment for the toast to be visible, then navigate
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAutosaveToggle = async (checked) => {
    try {
      setAutosaveEnabled(checked);
      await setDoc(doc(db, 'userPreferences', auth.currentUser.uid), {
        autosaveEnabled: checked
      }, { merge: true });
      toast.success(checked ? 'Autosave enabled' : 'Autosave disabled');
    } catch (error) {
      console.error('Error updating autosave preference:', error);
      toast.error('Failed to update autosave preference');
      setAutosaveEnabled(!checked); // Revert on error
    }
  };

  const handleAutoPdfToggle = async (checked) => {
    try {
      setAutoPdfEnabled(checked);
      await setDoc(doc(db, 'userPreferences', auth.currentUser.uid), {
        autoPdfEnabled: checked
      }, { merge: true });
      toast.success(checked ? 'Automatic PDF generation enabled' : 'Automatic PDF generation disabled');
    } catch (error) {
      console.error('Error updating PDF generation preference:', error);
      toast.error('Failed to update PDF generation preference');
      setAutoPdfEnabled(!checked); // Revert on error
    }
  };

  const handleDefaultPaymentToggle = async (checked) => {
    try {
      setDefaultPaymentStatus(checked);
      await setDoc(doc(db, 'userPreferences', auth.currentUser.uid), {
        defaultPaymentStatus: checked
      }, { merge: true });
      toast.success(checked ? 'New reports will default to paid status' : 'New reports will default to unpaid status');
    } catch (error) {
      console.error('Error updating default payment preference:', error);
      toast.error('Failed to update default payment preference');
      setDefaultPaymentStatus(!checked); // Revert on error
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">User Profile</h2>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-slate-600 hover:text-slate-800"
          >
            ← Back to Reports
          </button>
        </div>
        <form onSubmit={handleUpdateProfile} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Display Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your name"
              required
            />
          </div>
          
          <div>
            <p className="text-sm text-gray-500 mb-4">
              Email: {auth.currentUser?.email}
            </p>
          </div>

          <div className="flex items-center justify-between py-4 border-t border-gray-200">
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-gray-900">Autosave Drafts</h3>
              <p className="text-sm text-gray-500">
                Automatically save your work as drafts while typing
              </p>
            </div>
            <Switch
              checked={autosaveEnabled}
              onCheckedChange={handleAutosaveToggle}
              className="ml-4"
            />
          </div>

          <div className="flex items-center justify-between py-4 border-t border-gray-200">
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-gray-900">Automatic PDF Generation</h3>
              <p className="text-sm text-gray-500">
                Automatically generate PDF documents when creating reports
              </p>
            </div>
            <Switch
              checked={autoPdfEnabled}
              onCheckedChange={handleAutoPdfToggle}
              className="ml-4"
            />
          </div>

          <div className="flex items-center justify-between py-4 border-t border-gray-200">
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-gray-900">Default Payment Status</h3>
              <p className="text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  Set new reports as paid by default
                </span>
              </p>
            </div>
            <Switch
              checked={defaultPaymentStatus}
              onCheckedChange={handleDefaultPaymentToggle}
              className="ml-4"
            />
          </div>
          
          <div className="pt-6">
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default UserProfile; 